import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import api from '../services/api';
import Modal from '../components/Modal';
import FormInput from '../components/FormInput';
import Button from '../components/Button';
import Layout from '../components/Layout';
import './Dashboard.css';

function Dashboard() {
  const [user, setUser] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [orgForm, setOrgForm] = useState({ name: '' });
  const [projectForm, setProjectForm] = useState({ name: '', rootUrl: '' });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [deletingProject, setDeletingProject] = useState(null);
  const [startingCrawl, setStartingCrawl] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Poll for crawl status updates every 5 seconds
  useEffect(() => {
    if (!selectedOrg) return;

    const interval = setInterval(() => {
      loadProjects(selectedOrg.id);
    }, 5000);

    return () => clearInterval(interval);
  }, [selectedOrg?.id]);

  const loadData = async () => {
    try {
      const userData = authService.getCurrentUserFromStorage();
      setUser(userData);

      const orgsResponse = await api.get('/orgs');
      setOrganizations(orgsResponse.data.organizations);

      if (orgsResponse.data.organizations.length > 0) {
        const firstOrg = orgsResponse.data.organizations[0];
        setSelectedOrg(firstOrg);
        loadProjects(firstOrg.id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async (orgId) => {
    try {
      const response = await api.get(`/projects?organizationId=${orgId}`);
      setProjects(response.data.projects || []);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }

    setDeletingProject(projectId);
    try {
      await api.delete(`/projects/${projectId}?organizationId=${selectedOrg.id}`);
      await loadProjects(selectedOrg.id);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete project');
    } finally {
      setDeletingProject(null);
    }
  };

  const handleStartCrawl = async (projectId) => {
    setStartingCrawl(projectId);
    try {
      await api.post(`/crawls/projects/${projectId}/crawls/start`, {
        organizationId: selectedOrg.id,
        settings: {
          maxPages: 200,
          maxDepth: 3,
          respectRobots: true
        }
      });
      await loadProjects(selectedOrg.id);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to start crawl');
    } finally {
      setStartingCrawl(null);
    }
  };


  const handleCreateOrg = async (e) => {
    e.preventDefault();
    setErrors({});
    
    if (!orgForm.name.trim()) {
      setErrors({ name: 'Organization name is required' });
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/orgs', { name: orgForm.name.trim() });
      setOrganizations([...organizations, response.data.organization]);
      setSelectedOrg(response.data.organization);
      loadProjects(response.data.organization.id);
      setShowOrgModal(false);
      setOrgForm({ name: '' });
    } catch (error) {
      setErrors({ submit: error.response?.data?.error || 'Failed to create organization' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setErrors({});
    
    if (!selectedOrg) {
      setErrors({ submit: 'Please select an organization first' });
      return;
    }

    if (!projectForm.name.trim()) {
      setErrors({ name: 'Project name is required' });
      return;
    }

    if (!projectForm.rootUrl.trim()) {
      setErrors({ rootUrl: 'Website URL is required' });
      return;
    }

    // Basic URL validation
    try {
      new URL(projectForm.rootUrl.trim());
    } catch {
      setErrors({ rootUrl: 'Please enter a valid URL (e.g., https://example.com)' });
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/projects', {
        organizationId: selectedOrg.id,
        name: projectForm.name.trim(),
        rootUrl: projectForm.rootUrl.trim(),
      });
      setProjects([...projects, response.data.project]);
      setShowProjectModal(false);
      setProjectForm({ name: '', rootUrl: '' });
    } catch (error) {
      setErrors({ submit: error.response?.data?.error || 'Failed to create project' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Layout 
      organizations={organizations}
      selectedOrg={selectedOrg}
      onOrgSelect={(org) => {
        setSelectedOrg(org);
        loadProjects(org.id);
      }}
      onCreateOrg={() => setShowOrgModal(true)}
    >
      <div className="dashboard-page">
        <div className="page-header">
          <div>
            <h1 className="page-title">Projects</h1>
            <p className="page-subtitle">Manage and monitor your website audits</p>
          </div>
          <Button 
            variant="primary" 
            onClick={() => setShowProjectModal(true)}
            className="gradient-btn"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Create Project
          </Button>
        </div>

        <div className="dashboard-content">
          {selectedOrg ? (
            <div className="projects-section">
              <div className="projects-grid">
                {projects.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="9" y1="3" x2="9" y2="21"></line>
                        <line x1="3" y1="9" x2="21" y2="9"></line>
                      </svg>
                    </div>
                    <h3>No projects yet</h3>
                    <p>Create your first project to start auditing websites</p>
                    <Button variant="primary" onClick={() => setShowProjectModal(true)}>
                      Create Project
                    </Button>
                  </div>
                ) : (
                  projects.map((project) => (
                    <div key={project.id} className="project-card">
                      <div className="project-header">
                        <h3>{project.name}</h3>
                        {project.latestCrawlStatus && (
                          <span className={`status-badge status-${project.latestCrawlStatus.toLowerCase()}`}>
                            {project.latestCrawlStatus}
                          </span>
                        )}
                      </div>
                      <p className="project-url">{project.rootUrl}</p>
                      {project.totalCrawls > 0 && (
                        <p className="project-meta">
                          {project.totalCrawls} crawl{project.totalCrawls !== 1 ? 's' : ''}
                        </p>
                      )}
                      <div className="project-actions">
                        <div className="project-actions-row">
                          <Button 
                            variant="secondary" 
                            size="small"
                            onClick={() => navigate(`/project/${project.id}?orgId=${selectedOrg.id}`)}
                          >
                            View Details
                          </Button>
                          <Button 
                            variant="primary" 
                            size="small"
                            onClick={() => handleStartCrawl(project.id)}
                            loading={startingCrawl === project.id}
                            disabled={startingCrawl !== null}
                          >
                            {project.latestCrawlStatus ? 'Re-run' : 'Start'} Crawl
                          </Button>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="small"
                          onClick={() => handleDeleteProject(project.id)}
                          loading={deletingProject === project.id}
                          disabled={deletingProject !== null}
                          className="delete-button"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                </svg>
              </div>
              <h3>No organization selected</h3>
              <p>Select an organization from the sidebar to view projects</p>
              {organizations.length === 0 && (
                <Button variant="primary" onClick={() => setShowOrgModal(true)} className="gradient-btn">
                  Create Organization
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Organization Modal */}
      <Modal
        isOpen={showOrgModal}
        onClose={() => {
          setShowOrgModal(false);
          setOrgForm({ name: '' });
          setErrors({});
        }}
        title="Create Organization"
        size="small"
      >
        <form onSubmit={handleCreateOrg}>
          <FormInput
            label="Organization Name"
            value={orgForm.name}
            onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
            placeholder="My Agency"
            error={errors.name}
            required
            autoFocus
          />
          {errors.submit && <div className="form-error-message">{errors.submit}</div>}
          <div className="modal-actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setShowOrgModal(false);
                setOrgForm({ name: '' });
                setErrors({});
              }}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={submitting}>
              Create Organization
            </Button>
          </div>
        </form>
      </Modal>

      {/* Create Project Modal */}
      <Modal
        isOpen={showProjectModal}
        onClose={() => {
          setShowProjectModal(false);
          setProjectForm({ name: '', rootUrl: '' });
          setErrors({});
        }}
        title="Create Project"
        size="medium"
      >
        <form onSubmit={handleCreateProject}>
          <FormInput
            label="Project Name"
            value={projectForm.name}
            onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
            placeholder="My Website"
            error={errors.name}
            required
            autoFocus
          />
          <FormInput
            label="Website URL"
            type="url"
            value={projectForm.rootUrl}
            onChange={(e) => setProjectForm({ ...projectForm, rootUrl: e.target.value })}
            placeholder="https://example.com"
            error={errors.rootUrl}
            required
          />
          {errors.submit && <div className="form-error-message">{errors.submit}</div>}
          <div className="modal-actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setShowProjectModal(false);
                setProjectForm({ name: '', rootUrl: '' });
                setErrors({});
              }}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={submitting}>
              Create Project
            </Button>
          </div>
        </form>
      </Modal>
    </Layout>
  );
}

export default Dashboard;
